import { createAsyncThunk } from '@reduxjs/toolkit';
import ApiService from '../../../infrastructure/services/api/services';
import { apiConfig } from '../../../application/configurations/apiConfig';
import {
  ApiConfig
} from '../../../application/configurations/IConfig';
import { toggleLoading } from '../../components/pageLoader/loaderReducer';
import { appConstants } from '../../../application/configurations/constants';
import { getPreviewEmailFromUrl, UtilityService } from '../../../infrastructure/services/utils';
import { resultsConfigurations } from './config';
import { enableFlagForSuggestedOrRecentSearch } from './searchReducer';
import { store } from '../../../application/store';
import AnalyticsEvents from '../../../infrastructure/services/analytics/analyticsUseCase';
import {
  buildRefinementArrays,
  pushSearchQueryEvent,
  pushApplyRefinerEvent,
  pushPaginationEvent,
  pushPageSizeEvent
} from '../../../infrastructure/services/analyticsNew';
import JsonData from '../../../application/configurations/refiner.json';
import { setBrowsePageSearchFlag } from './searchReducer';
import { PeopleSortConfig } from '../../components/sort/config';
import resultsSearchActions from '../results/searchUseCase';
import envConfig, { isLocalOrDevEnv } from '../../../application/configurations/envConfig';
// COMMENTED OUT: Adobe Analytics - will be replaced with new Adobe Launch script
// import { AdobeAnalyticsEvents } from '../../../infrastructure/services/analytics/adobeAnalyticsUseCase';

// Module-level deduplication: prevents duplicate page-load analytics events
// caused by Browse's React effects dispatching doSearch twice on initial render.
let lastBrowsePageLoadTerm: string | null = null;
let lastBrowsePageLoadTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * class for processing async functions based on actions
 */
class Search {
  private searchApiConfig: ApiConfig;

  private analyticsApiConfig: ApiConfig;

  private hybridSearchApiConfig: ApiConfig;

  private isSemanticSearchEnabled: boolean = false;

  constructor() {
    this.searchApiConfig = { ...apiConfig.search };
    this.analyticsApiConfig = { ...apiConfig.analytics };
    this.hybridSearchApiConfig = { ...apiConfig.HybridSearch };
  }

  //Call search and hybrid search API based on toggle
  private async getSearchConfig(searchTerm: string, vertical: string) {
    // If toggle feature is enabled, set isSemanticSearchEnabled based on user's searchMode preference from API
      if (envConfig.isToggleAISearchEnabled) {
        this.isSemanticSearchEnabled = store.getState().browseSearch.searchModeBrowse === 'Semantic';
      } else {
        this.isSemanticSearchEnabled = false;
      }
        
      return this.hybridSearchApiConfig;
  }

  /**
   * async method with redux store middleware Thunk wrapper
   * this is used to access the action based api configuration and
   * add dynamic params then send to http request service.
   * this is also pass the response to
   * @param   {object} request the request object containing search query term & an optional start row.
   * @return  {object} return api response
   */
  doSearch = createAsyncThunk(
    'search',
    async (
      request: {
        searchTerm: string;
        vertical: string;
        filterByDate: number;
        accounts: Array<Object>;
        refinersClicked: any;
        flagForContentAge: boolean;
        flagForClear: boolean;
        startRow: number;
        browsePageSearch: boolean;
        sortByValue: {
          name: string;
          Value: string;
          Direction: number;
          SortType: number;
        };
        refinerValues: any;
        dropdownAction?: boolean;
        clearAll?: boolean;
        paginationAction?: 'Previous Page' | 'Next Page' | null;
        pageSizeAction?: number | null;
      },
      thunkApi
    ) => {
      // dispatching action to show loader
      thunkApi.dispatch(toggleLoading(true));
      let responseData: any = [];
      let resultCount: number = 0;
      let peopleAccountId: Array<string | undefined> = [];
      let profilePhotoCollection: any = [];
      let refinedVerticals: any = [];
      let latency: number = 0;
      let nestedRefinersFinal: any = [];
      let impersonationInfo: any = null;
      let verticalValue: string = '';
      let BrowserData: any = store.getState().browseSearch.browseConfigJSON;
      BrowserData.Verticals.forEach((vertical: any) => {
        if (request.vertical.toLowerCase() === vertical.Name) {
          verticalValue = vertical.filters.verticals;
        }
      });
      let vertical: string = verticalValue
        ? verticalValue
        : appConstants.verticals.Documents;

      const filterBrowseObject: any = BrowserData.Verticals.filter(
        (configValue: any) =>
          configValue.Name.toLowerCase() === request.vertical.toLowerCase()
      );
      let resultsLimit: any = resultsConfigurations.resultsPerPage.filter(
        (option) => option.vertical === vertical
      );
      let noOfItemsPerPage = JSON.parse(
        localStorage.getItem(appConstants.noOfItemsPerPage) || '{}'
      );
      let itemsToSetPerPage: number = noOfItemsPerPage
        ? noOfItemsPerPage
        : appConstants.defaultItemsPerPage;
      const modelObj = UtilityService.getRequestModel(vertical);
      const returnFields = UtilityService.getReturnFields(vertical);
      const searchConfig = {
        ...(await this.getSearchConfig(request.searchTerm, request.vertical))
      };
      searchConfig.accountInfo = request.accounts;
      searchConfig.data = {
        ...searchConfig.data,
        // using browseConfiguredSearchTerm method to add the search term with browse config
        Query: request.searchTerm,
        verticals: vertical,
        Refiners:
          filterBrowseObject && filterBrowseObject.length > 0
            ? Object.keys(filterBrowseObject[0].filters)
            : [appConstants.RefinerName],
        Size:
          request.startRow > resultsLimit[0].count - itemsToSetPerPage
            ? resultsLimit[0].count - request.startRow
            : itemsToSetPerPage,
        From: request.startRow ? request.startRow : 0,
        ReturnFields: returnFields,
        Sort: [],
        SearchType: this.isSemanticSearchEnabled
          ? appConstants.searchType.Semantic
          : appConstants.searchType.Keyword
      };

      // If URL contains a valid preview=<emailaddress> and the current browse
      // vertical is PolicyFinder, forward it as PreviewEmail to HybridSearch.
      // Helper handles HashRouter URLs and validates the email format so
      // malformed input is never forwarded. Authorization for who may
      // impersonate whom is enforced by the backend, not the client.
      if (
        request.vertical.toLowerCase() ===
        appConstants.nonVerticals.PolicyFinder.toLowerCase()
      ) {
        const previewEmail = getPreviewEmailFromUrl();
        if (previewEmail) {
          searchConfig.data.PreviewEmail = previewEmail;
        }
      }

      // filter search by Date of content age API call condition
      if (request.filterByDate && modelObj.contentAge) {
        searchConfig.data.Filters = {
          Condition: 'AND',
          Rules: [
            {
              Field: modelObj.contentAge,
              Type: 'Date',
              Operator: 'Range',
              Value: `${UtilityService.getMonthDates(
                request.filterByDate
              )};${new Date().toISOString()}`
            },
            {
              Field: 'ESC_EYCONTENTSTATUS',
              Type: 'String',
              Operator: ':',
              Values: ['Publish']
            }
          ]
        };
      }

      if (
        filterBrowseObject &&
        filterBrowseObject.length > 0 &&
        filterBrowseObject[0].QueryConditionFilters &&
        filterBrowseObject[0].QueryConditionFilters.length > 0
      ) {
        filterBrowseObject[0].QueryConditionFilters.forEach((item: any) => {
          searchConfig.data.Filters.Rules.push(item);
        });
      }

      // Refiners API call condition
      if (
        request.refinerValues !== undefined &&
        request.refinerValues.length > 0
      ) {
        request.refinerValues.forEach((item: any) => {
          UtilityService.selectedRefiners(
            item,
            searchConfig.data.Filters.Rules
          );
        });
      }
      //  sort by filter API call condition
      if (request.sortByValue && request.sortByValue !== undefined) {
        searchConfig.data.Sort.push({
          FieldName: request.sortByValue.Value,
          Ascending: request.sortByValue.Direction == 1 ? false : true
        });
        if (
          request.vertical === appConstants.verticals.People &&
          (request.sortByValue.Value ===
            PeopleSortConfig.filterOptions[1].Value ||
            request.sortByValue.Value ===
              PeopleSortConfig.filterOptions[3].Value)
        ) {
          searchConfig.data.Sort.push({
            FieldName:
              request.sortByValue.Value ===
              PeopleSortConfig.filterOptions[1].Value
                ? PeopleSortConfig.filterOptions[3].Value
                : PeopleSortConfig.filterOptions[1].Value,
            Ascending: request.sortByValue.Direction == 1 ? false : true
          });
        }
      }

      if (request.vertical === appConstants.verticals.All) {
        searchConfig.data.Filters.Rules.push({
          Field: appConstants.ElasticFields.vertical,
          Type: 'String',
          Operator: ':',
          Values: [
            appConstants.verticals.Credentials,
            appConstants.verticals.Data,
            appConstants.verticals.Documents,
            appConstants.verticals.News,
            appConstants.verticals.People,
            appConstants.verticals.Videos,
            appConstants.nonVerticals.Intranet
          ]
        });
      } else {
        let verticalText =
          request.vertical.toLowerCase() === appConstants.adobeAnalytics.browseConfigs.solutions.toLocaleLowerCase()
            ? appConstants.verticals.Documents
            : request.vertical;
            verticalText =
          request.vertical.toLowerCase() === appConstants.nonVerticals.PolicyFinder.toLocaleLowerCase()
            ? appConstants.verticals.Documents
            : verticalText;
        verticalText =
          request.vertical.toLowerCase() === appConstants.adobeAnalytics.browseConfigs.proposals.toLocaleLowerCase()
            ? appConstants.verticals.Documents
            : verticalText;
        verticalText =
          request.vertical.toLowerCase() === appConstants.adobeAnalytics.browseConfigs.credentials.toLocaleLowerCase()
            ? appConstants.verticals.Credentials
            : verticalText;
        searchConfig.data.Filters.Rules.push({
          Field: appConstants.ElasticFields.vertical,
          Type: 'String',
          Operator: ':',
          Value: verticalText
        });
      }

      await ApiService.requestApi([searchConfig])
        .then(
          (response: { [key: string]: any }) => {
            // Variable for backend failure counts i.e., isError property count in the actual API response object
            let errorCount: number = 0;
            // Extract impersonation flags so the UI can show the correct error message.
            if (response && response[0] && response[0].result) {
              const result = response[0].result;
              impersonationInfo = {
                userName: result.userName ?? null,
                previewMailValid: result.previewMailValid ?? null,
                isDataImpersonated: result.isDataImpersonated ?? null
              };
            }
            if (response && response[0] && response[0].result.relevantResults) {
              try {
                // Refiners
                // First Level
                let level1Transformation = UtilityService.convertRefinersLevel1(
                  JSON.parse(response[0].result.refinementResultsJSON)
                );

                // Second Level
                level1Transformation.map((refine: any) => {
                  let object1: any = {};
                  //Condition to check if the Refiner is from AEM using path keyword.
                  if (refine.title.indexOf('UFPATH') > -1) {
                    object1 = {
                      title: refine.title,
                      refId: refine.refId,
                      ID: refine.ID,
                      source: UtilityService.convertRefinersLevel2({
                        RefinementResults: refine.source.slice(1)
                      })
                    };
                  } else {
                    object1 = {
                      title: refine.title,
                      refId: refine.refId,
                      ID: refine.ID,
                      source: UtilityService.convertRefinersLevel2({
                        RefinementResults: refine.source
                      })
                    };
                  }

                  object1.title =
                    filterBrowseObject[0] &&
                    filterBrowseObject[0].filters[object1.title] !== undefined
                      ? filterBrowseObject[0].filters[object1.title]
                      : object1.title;

                  nestedRefinersFinal.push(object1);
                });

                // Dont remove this console until we implement Refiners.
                //console.log(nestedRefinersFinal);
              } catch (error) {
                if (isLocalOrDevEnv) {
                  console.log(error);
                }
              }
              // Refiners logics ends here.

              response[0].result.relevantResults.forEach((record: any) => {
                peopleAccountId.push(record.accountId);
                 responseData.push(
                    UtilityService.getResponseModel(record.vertical, record)
                  );
              });
              // Setting up the Results Found count by decreasing with the backend failure count(errorCount).
              // This will fix for the backend failure scenarios i.e., when the backend returns any error in the response.
              resultCount =
                responseData.length > 0
                  ? response[0].result.totalRelevantResults - errorCount
                  : responseData.length;
              refinedVerticals = response[0].result.refinementResults;
              latency = response[0].result.totalTime;
              const validPeopleAccountIds = peopleAccountId.filter((id) => id);
              if (validPeopleAccountIds.length) {
                thunkApi.dispatch(
                  resultsSearchActions.getUserProfilePic({
                    peopleAccountIds: validPeopleAccountIds,
                    accounts: request.accounts
                  })
                );
              }
            }
          },
          (error) => {
            throw new Error(error);
          }
        )
        .finally(() => {
          // Insert query recommendation for search history tracking
          if (resultCount && resultCount > 0) {
            this.insertQueryRecommendation(
              request.searchTerm,
              request.vertical,
              store.getState().general.userInfo?.userEmail || '',
              request.accounts
            );
          }

          if (request.paginationAction) {
            const itemsPerPage = Number(
              localStorage.getItem(appConstants.noOfItemsPerPage) ||
                appConstants.defaultItemsPerPage
            );
            const pageLimit =
              resultsConfigurations.resultsPerPage.find(
                (option) => option.vertical.toLowerCase() === request.vertical.toLowerCase()
              )?.count ?? resultCount;
            const totalPages = Math.max(
              1,
              Math.ceil(Math.min(resultCount, pageLimit) / itemsPerPage)
            );
            const currentPage = Math.floor(request.startRow / itemsPerPage) + 1;

            const paginationLabelMap: Record<string, string> | undefined =
              filterBrowseObject && filterBrowseObject.length > 0
                ? filterBrowseObject[0].filters
                : undefined;
            const { refinementType, refinementValue } = buildRefinementArrays(
              request.refinerValues ?? [],
              request.vertical,
              paginationLabelMap
            );
            pushPaginationEvent(
              request.paginationAction,
              currentPage,
              totalPages,
              request.searchTerm,
              resultCount,
              request.vertical,
              refinementType,
              refinementValue
            );
          } else if (request.pageSizeAction) {
            const pageSizeLabelMap: Record<string, string> | undefined =
              filterBrowseObject && filterBrowseObject.length > 0
                ? filterBrowseObject[0].filters
                : undefined;
            const { refinementType, refinementValue } = buildRefinementArrays(
              request.refinerValues ?? [],
              request.vertical,
              pageSizeLabelMap
            );
            pushPageSizeEvent(
              request.pageSizeAction,
              request.searchTerm,
              resultCount,
              request.vertical,
              refinementType,
              refinementValue
            );
          } else {
            // Calling the Analytics Events
            this.callAnalytics(request, resultCount);

            // Adobe Analytics: push search query click-action event
            // Browse page only supports Search and Suggested Search (no recent search popup).
            const browseSearchFlag = store.getState().browseSearch.flagForSuggestedOrRecentSearch;
          // Refiner-triggered searches push their own refiner event instead of a Search event.
          const isRefinerTriggered =
            !!request.clearAll ||
            !!(
              request.refinersClicked &&
              (request.flagForContentAge || request.flagForClear)
            );
          if (!request.paginationAction && (isRefinerTriggered || request.dropdownAction)) {
            // no Search event
          } else if (!request.paginationAction && browseSearchFlag !== '') {
            // User-initiated search (enter key, button click, or suggestion) — always fire.
            const browseQueryName: 'Search' | 'Suggested Search' =
              browseSearchFlag === 'Suggestions Click' ? 'Suggested Search' : 'Search';
            pushSearchQueryEvent(browseQueryName, request.searchTerm, resultCount);
          } else if (!request.paginationAction) {
            // Page-load/automatic search — deduplicate to fire only once per unique term.
            if (lastBrowsePageLoadTerm !== request.searchTerm) {
              lastBrowsePageLoadTerm = request.searchTerm;
              if (lastBrowsePageLoadTimer) clearTimeout(lastBrowsePageLoadTimer);
              lastBrowsePageLoadTimer = setTimeout(() => {
                lastBrowsePageLoadTerm = null;
              }, 500);
              pushSearchQueryEvent('Search', request.searchTerm, resultCount);
            }
          }
          }

          // Adobe Analytics: push apply / clear refiner click-action event
          if (
            !request.paginationAction &&
            !request.pageSizeAction &&
            request.refinersClicked &&
            (request.flagForContentAge || request.flagForClear) &&
            !request.clearAll
          ) {
            // refinersClicked is set as a plain object {key:[]} or an array [{key:[]}] depending
            // on whether the store already had refiners — normalise to array format.
            const clickedArr: Record<string, string[]>[] = Array.isArray(request.refinersClicked)
              ? request.refinersClicked
              : [request.refinersClicked];
            const refinementType: string[] = [];
            const refinementValue: string[] = [];
            // Browse renders filter labels from the vertical's browseConfig `filters` map,
            // so use it (falling back to refiner.json) to report the names shown in the UI.
            const verticalRefinerMap: Record<string, string | undefined> =
              (filterBrowseObject &&
                filterBrowseObject.length > 0 &&
                filterBrowseObject[0].filters) ||
              JsonData.find(
                (item: any) =>
                  item.vertical.toLowerCase() === request.vertical.toLowerCase()
              ) ||
              {};
            clickedArr.forEach((refinerObj: Record<string, string[]>) => {
              const key = Object.keys(refinerObj)[0];
              const values: string[] = Object.values(refinerObj)[0];
              if (key && values && values.length > 0) {
                const displayName: string = verticalRefinerMap[key] ?? key;
                refinementType.push(displayName);
                values.forEach((val: string) => {
                  const leafValue = val.includes('\\') ? val.split('\\').pop()! : val;
                  refinementValue.push(leafValue);
                });
              }
            });
            if (refinementType.length > 0) {
              if (request.flagForClear) {
                // Clear button clicked inside a filter dropdown (checked first — takes priority
                // over stale flagForContentAge which may still be true from a previous Apply)
                pushApplyRefinerEvent(
                  request.searchTerm,
                  resultCount,
                  request.vertical,
                  refinementType,
                  refinementValue,
                  { name: 'Clear Refiner', controlType: 'button', includeNullInstances: false }
                );
              } else if (request.flagForContentAge) {
                // Apply button clicked
                pushApplyRefinerEvent(
                  request.searchTerm,
                  resultCount,
                  request.vertical,
                  refinementType,
                  refinementValue
                );
              }
            }
          }

          // injecting page load adobe Analytics Events
          // COMMENTED OUT: Adobe Analytics - will be replaced with new Adobe Launch script
          // AdobeAnalyticsEvents.injectAdobePageLoadEvents(
          //   request.searchTerm,
          //   resultCount,
          //   store.getState().general.userInfo,
          //   request.vertical,
          //   false
          // );

          // dispatching action to hide loader
          thunkApi.dispatch(toggleLoading(false));
          thunkApi.dispatch(setBrowsePageSearchFlag(true));
          thunkApi.dispatch(enableFlagForSuggestedOrRecentSearch(''));
        });

      return {
        results: responseData,
        resultCount: resultCount,
        profilePhotoCollection: profilePhotoCollection,
        refiners: refinedVerticals,
        latency: latency,
        nestedRefiners: nestedRefinersFinal,
        isSemanticSearchEnabled: this.isSemanticSearchEnabled,
        impersonationInfo: impersonationInfo
      };
    }
  );

  /**
   * Method to insert query recommendations for search analytics
   * Called alongside Hybrid API on every search (Enter, Tab, etc.)
   * @param   {string} searchTerm  search query term
   * @param   {string} vertical  search vertical
   * @param   {string} userEmail  hashed user email from store
   * @param   {Array<Object>} accounts accounts  msal accounts
   */
  insertQueryRecommendation = async (
    searchTerm: string,
    vertical: string,
    userEmail: string,
    accounts: Array<Object>
  ): Promise<void> => {
    try {
      const queryRecommendationConfig = { ...apiConfig.queryRecommendationsInsert };
      queryRecommendationConfig.accountInfo = accounts;
      queryRecommendationConfig.data = {
        Time: new Date().toISOString(),
        Email: userEmail,
        Query: searchTerm,
        Vertical: vertical
      };
      await ApiService.requestApi([queryRecommendationConfig]);
    } catch (error) {
      if (isLocalOrDevEnv) {
        console.warn('Failed to insert query recommendation:', error);
      }
    }
  };

  /**
   * Method to trigger analytics actions
   * this is used to access the action based api configuration and
   * add dynamic params then send to http request service.
   * @param  {object} request the request object containing search query term & an optional start row.
   * @param  {number} resultCount search result count
   */
  callAnalytics = (
    request: {
      searchTerm: string;
      vertical: string;
      filterByDate?: number;
      accounts: Array<Object>;
      startRow?: number | undefined;
      browsePageSearch: boolean;
      refinersClicked?: Array<Object>;
      flagForContentAge?: boolean;
      flagForClear?: boolean;
    },
    resultCount: number | undefined
  ) => {
    const flagForSuggestedOrRecentSearch =
      store.getState().browseSearch.flagForSuggestedOrRecentSearch;

    if (flagForSuggestedOrRecentSearch !== '') {
      const wasRecentSearch =
        flagForSuggestedOrRecentSearch ===
        resultsConfigurations.RecentSearchClick
          ? 'true'
          : 'false';
      // Calling the RecentSearch and Suggestion Click analytics usecase
      AnalyticsEvents.recentSearchAndSuggestionsClick(
        request.searchTerm,
        request.accounts,
        request.browsePageSearch,
        wasRecentSearch,
        resultCount
      );
    } else {
      // Calling the Search Action analytics usecase
      AnalyticsEvents.searchAction(
        request.searchTerm,
        request.accounts,
        request.browsePageSearch,
        request.vertical,
        resultCount
      );
    }
    if (request.vertical !== appConstants.verticals.People) {
      AnalyticsEvents.refinerAction(
        request.searchTerm,
        request.accounts,
        request.browsePageSearch,
        request.filterByDate,
        request.refinersClicked,
        request.flagForContentAge,
        request.flagForClear,
        resultCount
      );
    }
  };

  /**
   * async method to trigger suggestions search action
   * this is used to access the action based api configuration and
   * add dynamic params then send to http request service.
   * this is also pass the response to
   * @param   {string} searchTerm  search query term
   */
  queryRecommondations = createAsyncThunk(
    'queryRecommondations',
    async (request: {
      searchTerms: string;
      visibleSuggestions: number;
      vertical: string;
    }) => {
      const suggestionsConfig = { ...apiConfig.querySuggestions };
      suggestionsConfig.data.Query = request.searchTerms;
      suggestionsConfig.data.Vertical = request.vertical;
      let responseData: any = [];
      await ApiService.requestApi([suggestionsConfig])
        .then((suggestionsArray: any) => {
          if (suggestionsArray.length > 0) {
            suggestionsArray[0].map((item: any) => {
              responseData.push(item);
            });
          }
        })
        .catch((error) => {
          if (isLocalOrDevEnv) {
            console.log('Error' + error);
          }
        });

      return {
        suggestionList: responseData
      };
    }
  );

  // Fetch user preferences to get initial searchMode 
  public fetchUserPreferences = createAsyncThunk<{ searchMode: 'Keyword' | 'Semantic' }, { accounts: Array<Object> }>(
      'browse/fetchUserPreferences',
      async (request) => {
        // If toggle AI search feature is disabled, always return Keyword
        if (!envConfig.isToggleAISearchEnabled) {
          return { searchMode: 'Keyword' as const };
        }
        try {
          const userPreferencesConfig = { ...apiConfig.getUserProfileProperties };
          userPreferencesConfig.accountInfo = request.accounts;
          const response = await ApiService.requestApi([userPreferencesConfig]);
          if (response && response[0]) {
            const preferences = response[0] as any;
            const newSearchMode = preferences.enableAISearch ? 'Semantic' as const : 'Keyword' as const;
            
            return { searchMode: newSearchMode };
          }
          
          return { searchMode: 'Keyword' as const };
        } catch (error) {
          if (isLocalOrDevEnv) {
          console.warn('Failed to fetch user preferences on load:', error);
          }
          
          return { searchMode: 'Keyword' as const };
        }
      }
    );
  // Update user's search preference for AI Search and persist it through API call

  public updateToggleAISearch = createAsyncThunk(
    'browse/updateToggleAISearch',
    async (request: { accounts: Array<Object>; enableAISearch: boolean }) => {
      try {
        const toggleConfig = { ...apiConfig.toggleAISearch };
        toggleConfig.accountInfo = request.accounts;
        toggleConfig.data = { enableAISearch: request.enableAISearch };
        await ApiService.requestApi([toggleConfig]);
      } catch (error) {
        if (isLocalOrDevEnv) {
          console.warn('Failed to update AI search preference:', error);
        }
      }
    }
  );
}

const SearchUseCase = new Search();
export default SearchUseCase;
